import { useEffect, useMemo, useRef, useState } from 'react';
import {
  cloneRig,
  inverse,
  point,
  setJointBind,
  transform,
  type Vec3,
  type RigFrame,
  type RigGeometry,
} from '../../lib/rig-doc.js';
import { useRig } from '../../state/rig.js';

type Camera = { yaw: number; pitch: number; scale: number; target: Vec3; radius: number };
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
function axes(c: Camera): [Vec3, Vec3, Vec3] {
  const y = c.yaw,
    p = c.pitch;
  return [
    [Math.cos(y), 0, -Math.sin(y)],
    [Math.sin(y) * Math.sin(p), Math.cos(p), Math.cos(y) * Math.sin(p)],
    [Math.sin(y) * Math.cos(p), -Math.sin(p), Math.cos(y) * Math.cos(p)],
  ];
}
function project(p: Vec3, c: Camera, w: number, h: number): [number, number, number] {
  const [r, u, f] = axes(c),
    q = p.map((v, i) => v - c.target[i]!) as Vec3;
  return [w / 2 + dot(q, r) * c.scale, h / 2 - dot(q, u) * c.scale, dot(q, f)];
}
function delta(dx: number, dy: number, c: Camera): Vec3 {
  const [r, u] = axes(c);
  return r.map((v, i) => (v * dx - u[i]! * dy) / c.scale) as Vec3;
}

class MeshRenderer {
  gl: WebGL2RenderingContext;
  program: WebGLProgram;
  matrix: WebGLUniformLocation | null;
  heat: WebGLUniformLocation | null;
  wire: WebGLUniformLocation | null;
  ghost: WebGLUniformLocation | null;
  meshes: {
    vao: WebGLVertexArrayObject;
    vertex: WebGLBuffer;
    indices: WebGLBuffer;
    edges: WebGLBuffer;
    count: number;
    edgeCount: number;
    color: number[];
  }[] = [];
  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: true, alpha: false });
    if (!gl) throw new Error('WebGL 2 is needed for the rig preview.');
    this.gl = gl;
    const shader = (type: number, source: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, source);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS))
        throw new Error(gl.getShaderInfoLog(s) ?? 'Shader error');
      return s;
    };
    const vs = shader(
      gl.VERTEX_SHADER,
      `#version 300 es
      layout(location=0) in vec3 position;layout(location=1) in float weight;uniform mat4 camera;out vec3 world;out float influence;
      void main(){world=position;influence=weight;gl_Position=camera*vec4(position,1.);}`,
    );
    const fs = shader(
      gl.FRAGMENT_SHADER,
      `#version 300 es
      precision highp float;in vec3 world;in float influence;uniform vec3 color;uniform bool heat;uniform bool wire;uniform vec4 ghost;out vec4 outColor;
      void main(){vec3 n=normalize(cross(dFdx(world),dFdy(world)));float light=.4+.6*abs(dot(n,normalize(vec3(.4,.8,.6))));
      vec3 ramp=mix(vec3(.08,.16,.48),vec3(.1,.85,.65),clamp(influence*2.,0.,1.));ramp=mix(ramp,vec3(1.,.28,.1),clamp(influence*2.-1.,0.,1.));
      outColor=ghost.a>0.?ghost:vec4(wire?vec3(.09,.12,.18):(heat?ramp:color)*light,1.);}`,
    );
    this.program = gl.createProgram()!;
    gl.attachShader(this.program, vs);
    gl.attachShader(this.program, fs);
    gl.linkProgram(this.program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS))
      throw new Error('Could not initialize rig preview.');
    this.matrix = gl.getUniformLocation(this.program, 'camera');
    this.heat = gl.getUniformLocation(this.program, 'heat');
    this.wire = gl.getUniformLocation(this.program, 'wire');
    this.ghost = gl.getUniformLocation(this.program, 'ghost');
  }
  clear() {
    const gl = this.gl;
    for (const m of this.meshes) {
      gl.deleteVertexArray(m.vao);
      gl.deleteBuffer(m.vertex);
      gl.deleteBuffer(m.indices);
      gl.deleteBuffer(m.edges);
    }
    this.meshes = [];
  }
  load(geometry: RigGeometry) {
    this.clear();
    const gl = this.gl;
    this.meshes = geometry.meshes.map((m) => {
      const vao = gl.createVertexArray()!,
        vertex = gl.createBuffer()!,
        indices = gl.createBuffer()!,
        edges = gl.createBuffer()!;
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, vertex);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 16, 0);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, 1, gl.FLOAT, false, 16, 12);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indices);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(m.indices), gl.STATIC_DRAW);
      const edge: number[] = [];
      for (let i = 0; i < m.indices.length; i += 3) {
        const a = m.indices[i]!,
          b = m.indices[i + 1]!,
          c = m.indices[i + 2]!;
        edge.push(a, b, b, c, c, a);
      }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, edges);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(edge), gl.STATIC_DRAW);
      return {
        vao,
        vertex,
        indices,
        edges,
        count: m.indices.length,
        edgeCount: edge.length,
        color: m.color,
      };
    });
  }
  draw(
    geometry: RigGeometry,
    frame: RigFrame,
    c: Camera,
    w: number,
    h: number,
    joint: number,
    heat: boolean,
    wire: boolean,
    ghosts: OnionGhost[] = [],
  ) {
    const gl = this.gl;
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.clearColor(0.055, 0.065, 0.085, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.useProgram(this.program);
    const [r, u, f] = axes(c),
      sx = (2 * c.scale) / w,
      sy = (2 * c.scale) / h,
      sz = -1 / (c.radius * 20);
    gl.uniformMatrix4fv(
      this.matrix,
      false,
      new Float32Array([
        r[0] * sx,
        u[0] * sy,
        f[0] * sz,
        0,
        r[1] * sx,
        u[1] * sy,
        f[1] * sz,
        0,
        r[2] * sx,
        u[2] * sy,
        f[2] * sz,
        0,
        -dot(c.target, r) * sx,
        -dot(c.target, u) * sy,
        -dot(c.target, f) * sz,
        1,
      ]),
    );
    gl.uniform1i(this.heat, Number(heat));
    gl.uniform4f(this.ghost, 0, 0, 0, 0);
    this.meshes.forEach((m, mi) => {
      const mesh = geometry.meshes[mi]!,
        positions = frame.positions[mi] ?? [],
        data = new Float32Array(positions.length * 4);
      positions.forEach((p, vi) => {
        data.set(p, vi * 4);
        data[vi * 4 + 3] = (mesh.weights[vi] ?? []).reduce(
          (sum, v, slot) => sum + (mesh.joints[vi]?.[slot] === joint ? v : 0),
          0,
        );
      });
      gl.bindVertexArray(m.vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, m.vertex);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
      gl.uniform3fv(gl.getUniformLocation(this.program, 'color'), m.color.slice(0, 3));
      gl.uniform1i(this.wire, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.indices);
      gl.enable(gl.POLYGON_OFFSET_FILL);
      gl.polygonOffset(1, 1);
      gl.drawElements(gl.TRIANGLES, m.count, gl.UNSIGNED_INT, 0);
      gl.disable(gl.POLYGON_OFFSET_FILL);
      if (wire) {
        gl.uniform1i(this.wire, 1);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.edges);
        gl.drawElements(gl.LINES, m.edgeCount, gl.UNSIGNED_INT, 0);
      }
    });
    // Onion skin: other frames as tinted wireframes behind the current pose.
    for (const g of ghosts)
      this.meshes.forEach((m, mi) => {
        const positions = g.positions[mi];
        if (!positions?.length) return;
        const data = new Float32Array(positions.length * 4);
        positions.forEach((p, vi) => data.set(p, vi * 4));
        gl.bindVertexArray(m.vao);
        gl.bindBuffer(gl.ARRAY_BUFFER, m.vertex);
        gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
        gl.uniform4f(this.ghost, g.color[0], g.color[1], g.color[2], 1);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, m.edges);
        gl.drawElements(gl.LINES, m.edgeCount, gl.UNSIGNED_INT, 0);
      });
    gl.uniform4f(this.ghost, 0, 0, 0, 0);
    gl.bindVertexArray(null);
  }
  dispose() {
    this.clear();
    this.gl.deleteProgram(this.program);
  }
}

/** One onion-skin frame: skinned positions per mesh and a tint. */
export interface OnionGhost {
  positions: Vec3[][];
  color: [number, number, number];
}

export function RigViewport({
  tool,
  axis,
  radius,
  strength,
  subtract,
  through,
  showBones,
  wire,
  onAdd,
}: {
  tool: 'move' | 'rotate' | 'scale' | 'add';
  axis: 'free' | 'x' | 'y' | 'z';
  radius: number;
  strength: number;
  subtract: boolean;
  through: boolean;
  showBones: boolean;
  wire: boolean;
  onAdd: (position: Vec3) => void;
}) {
  const geometry = useRig((s) => s.geometry),
    frame = useRig((s) => s.frame),
    selected = useRig((s) => s.selected),
    mode = useRig((s) => s.mode),
    rig = useRig((s) => s.rig),
    ghosts = useRig((s) => s.ghosts);
  const host = useRef<HTMLDivElement>(null),
    canvas = useRef<HTMLCanvasElement>(null),
    renderer = useRef<MeshRenderer | null>(null);
  const [size, setSize] = useState<[number, number]>([700, 500]),
    [camera, setCamera] = useState<Camera>({
      yaw: 0.35,
      pitch: 0.12,
      scale: 150,
      target: [0, 0.8, 0],
      radius: 1,
    }),
    [renderError, setRenderError] = useState(''),
    [cursor, setCursor] = useState<[number, number] | null>(null);
  const drag = useRef<{
    kind: 'orbit' | 'pan' | 'joint' | 'paint';
    x: number;
    y: number;
    camera: Camera;
    pose: ReturnType<typeof transform>;
    history: boolean;
    last: number;
  } | null>(null);
  const fitted = useRef<string | null>(null);
  const previousSize = useRef(size);
  useEffect(() => {
    const previous = previousSize.current;
    previousSize.current = size;
    if (previous[0] > 0 && previous[1] > 0 && size[0] > 0 && size[1] > 0)
      setCamera((c) => ({ ...c, scale: (c.scale * Math.min(...size)) / Math.min(...previous) }));
  }, [size]);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize([entry.contentRect.width, entry.contentRect.height]);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!canvas.current) return;
    try {
      renderer.current = new MeshRenderer(canvas.current);
    } catch (e) {
      setRenderError(String(e));
    }
    return () => {
      renderer.current?.dispose();
      renderer.current = null;
    };
  }, []);
  const fit = () => {
    const points = geometry?.frame.positions.flat() ?? [];
    if (!points.length) return;
    const min = [Infinity, Infinity, Infinity],
      max = [-Infinity, -Infinity, -Infinity];
    for (const p of points)
      for (let i = 0; i < 3; i++) {
        min[i] = Math.min(min[i]!, p[i]!);
        max[i] = Math.max(max[i]!, p[i]!);
      }
    const extent = Math.max(...max.map((v, i) => v - min[i]!), 0.1);
    setCamera((c) => ({
      ...c,
      target: min.map((v, i) => (v + max[i]!) / 2) as Vec3,
      scale: (Math.min(size[0], size[1]) * 0.7) / extent,
      radius: extent,
    }));
  };
  useEffect(() => {
    if (geometry) renderer.current?.load(geometry);
    if (fitted.current !== useRig.getState().path && geometry) {
      fitted.current = useRig.getState().path;
      fit();
    }
  }, [geometry]);
  useEffect(() => {
    if (!canvas.current || !geometry || !frame) return;
    const dpr = Math.min(devicePixelRatio, 2);
    canvas.current.width = Math.round(size[0] * dpr);
    canvas.current.height = Math.round(size[1] * dpr);
    renderer.current?.draw(
      geometry,
      frame,
      camera,
      ...size,
      rig.joints.findIndex((j) => j.name === selected),
      mode === 'skin',
      wire,
      mode === 'animate' ? ghosts : [],
    );
  }, [geometry, frame, camera, size, selected, mode, rig.joints, wire, ghosts]);
  const joints = useMemo(
    () => frame?.joints.map((j) => ({ ...j, screen: project(j.position, camera, ...size) })) ?? [],
    [frame, camera, size],
  );
  function paint(x: number, y: number, first: boolean) {
    const s = useRig.getState();
    if (!s.geometry || !s.frame || !s.selected || (s.geometry.sourceSkinned && !s.skinUnlocked))
      return;
    let surface = -Infinity,
      hitMesh = -1;
    const projected = s.frame.positions.map((ps) => ps.map((p) => project(p, camera, ...size)));
    projected.forEach((ps, mi) => {
      const indices = s.geometry!.meshes[mi]!.indices;
      for (let i = 0; i < indices.length; i += 3) {
        const a = ps[indices[i]!]!,
          b = ps[indices[i + 1]!]!,
          c = ps[indices[i + 2]!]!;
        const det = (b[1] - c[1]) * (a[0] - c[0]) + (c[0] - b[0]) * (a[1] - c[1]);
        if (Math.abs(det) < 1e-8) continue;
        const u = ((b[1] - c[1]) * (x - c[0]) + (c[0] - b[0]) * (y - c[1])) / det,
          v = ((c[1] - a[1]) * (x - c[0]) + (a[0] - c[0]) * (y - c[1])) / det;
        if (u >= 0 && v >= 0 && u + v <= 1) {
          const z = u * a[2] + v * b[2] + (1 - u - v) * c[2];
          if (z > surface) {
            surface = z;
            hitMesh = mi;
          }
        }
      }
    });
    const changes: { mi: number; vertices: { index: number; strength: number }[] }[] = [];
    projected.forEach((ps, mi) => {
      const mesh = s.geometry!.meshes[mi]!;
      if (s.node && mesh.node !== s.node) return;
      if (!through && mi !== hitMesh) return;
      const vertices = ps.flatMap((p, index) => {
        const d = Math.hypot(p[0] - x, p[1] - y);
        return d <= radius && (through || Math.abs(p[2] - surface) < (radius / camera.scale) * 0.7)
          ? [{ index, strength: 1 - d / radius }]
          : [];
      });
      if (vertices.length) changes.push({ mi, vertices });
    });
    if (!changes.length) return;
    if (first) useRig.setState({ past: [...s.past, cloneRig(s.rig)].slice(-80), future: [] });
    for (const c of changes)
      useRig.getState().paint(c.mi, c.vertices, (subtract ? -1 : 1) * strength);
  }
  return (
    <div className="rig-viewport" ref={host} onContextMenu={(e) => e.preventDefault()}>
      <canvas ref={canvas} aria-label="Rigged model preview" />
      <svg
        className="rig-overlay"
        viewBox={`0 0 ${size[0]} ${size[1]}`}
        aria-label="Skeleton and joint handles"
        onWheel={(e) => {
          e.preventDefault();
          setCamera((c) => ({
            ...c,
            scale: Math.max(0.5, Math.min(50000, c.scale * Math.exp(-e.deltaY * 0.001))),
          }));
        }}
        onPointerDown={(e) => {
          const rect = e.currentTarget.getBoundingClientRect(),
            x = e.clientX - rect.left,
            y = e.clientY - rect.top;
          const s = useRig.getState();
          e.currentTarget.setPointerCapture(e.pointerId);
          const joint = (e.target as Element).getAttribute('data-joint');
          if (joint) s.select(joint);
          if (tool === 'add' && mode === 'skeleton' && e.button === 0 && !e.altKey) {
            const d = delta(x - size[0] / 2, y - size[1] / 2, camera);
            onAdd(d.map((v, i) => v + camera.target[i]!) as Vec3);
            return;
          }
          const kind =
            e.button === 1 || (e.shiftKey && e.altKey)
              ? 'pan'
              : e.button === 2 || e.altKey
                ? 'orbit'
                : joint
                  ? 'joint'
                  : mode === 'skin'
                    ? 'paint'
                    : 'orbit';
          drag.current = {
            kind,
            x,
            y,
            camera,
            pose:
              mode === 'skeleton'
                ? transform(s.rig.joints.find((j) => j.name === (joint ?? s.selected))?.bind)
                : transform(useRig.getState().pose ?? undefined),
            history: false,
            last: performance.now(),
          };
          if (kind === 'paint') {
            paint(x, y, true);
            drag.current.history = true;
          }
        }}
        onPointerMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect(),
            x = e.clientX - rect.left,
            y = e.clientY - rect.top;
          setCursor([x, y]);
          const d = drag.current;
          if (!d) return;
          const dx = x - d.x,
            dy = y - d.y;
          if (d.kind === 'orbit') {
            setCamera({
              ...d.camera,
              yaw: d.camera.yaw - dx * 0.008,
              pitch: Math.max(-1.5, Math.min(1.5, d.camera.pitch + dy * 0.008)),
            });
            return;
          }
          if (d.kind === 'pan') {
            const change = delta(-dx, -dy, d.camera);
            setCamera({
              ...d.camera,
              target: d.camera.target.map((v, i) => v + change[i]!) as Vec3,
            });
            return;
          }
          if (performance.now() - d.last < 65) return;
          d.last = performance.now();
          if (d.kind === 'paint') {
            paint(x, y, !d.history);
            d.history = true;
            return;
          }
          const s = useRig.getState();
          if (s.geometry?.sourceSkinned && !s.skinUnlocked) return;
          const pose = transform(d.pose);
          const change = delta(dx, dy, d.camera);
          if (tool === 'move') {
            if (axis !== 'free')
              for (let i = 0; i < 3; i++) if (i !== 'xyz'.indexOf(axis)) change[i] = 0;
            let local = change;
            if (mode === 'animate') {
              const parent = s.frame?.joints.find(
                (j) => j.name === s.frame?.joints.find((j) => j.name === s.selected)?.parent,
              );
              if (parent) local = point(inverse(parent.global), change, true);
            }
            pose.position = pose.position.map((v, i) => v + local[i]!) as Vec3;
          } else if (tool === 'rotate') {
            const i = axis === 'free' ? 2 : 'xyz'.indexOf(axis);
            pose.rotation[i]! += dx * 0.5;
          } else if (tool === 'scale') {
            const factor = Math.max(0.01, 1 + dx * 0.005);
            pose.scale = pose.scale.map((v, i) =>
              axis === 'free' || i === 'xyz'.indexOf(axis) ? v * factor : v,
            ) as Vec3;
          }
          if (mode === 'skeleton' && s.selected) {
            s.commit(setJointBind(s.rig, s.selected, pose), !d.history);
            d.history = true;
          } else if (mode === 'animate') s.editPose(pose);
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onPointerLeave={() => setCursor(null)}
      >
        {showBones &&
          joints.map((j) => {
            const parent = joints.find((p) => p.name === j.parent);
            return (
              <g key={j.name}>
                {parent && (
                  <line
                    x1={parent.screen[0]}
                    y1={parent.screen[1]}
                    x2={j.screen[0]}
                    y2={j.screen[1]}
                    stroke={j.name === selected ? '#ffcc66' : '#88d8ee'}
                    strokeWidth={j.name === selected ? 5 : 3}
                    opacity=".9"
                  />
                )}
                <circle
                  data-joint={j.name}
                  cx={j.screen[0]}
                  cy={j.screen[1]}
                  r={j.name === selected ? 8 : 5}
                  fill={j.name === selected ? '#ffcc66' : '#9aeaff'}
                  stroke="#18232f"
                  strokeWidth="2"
                >
                  <title>{j.name}</title>
                </circle>
                {j.name === selected && (
                  <text
                    x={j.screen[0] + 12}
                    y={j.screen[1] - 10}
                    fill="#ffe2a2"
                    fontSize="12"
                    pointerEvents="none"
                  >
                    {j.name}
                  </text>
                )}
              </g>
            );
          })}
        {mode === 'skin' && cursor && (
          <circle
            cx={cursor[0]}
            cy={cursor[1]}
            r={radius}
            fill="none"
            stroke={subtract ? '#ff806b' : '#8ee7be'}
            strokeDasharray="5 3"
            pointerEvents="none"
          />
        )}
      </svg>
      <div className="rig-view-tools">
        <button onClick={fit}>Frame model</button>
        <button onClick={() => setCamera((c) => ({ ...c, yaw: 0, pitch: 0 }))}>Front</button>
        <button onClick={() => setCamera((c) => ({ ...c, yaw: Math.PI / 2, pitch: 0 }))}>
          Side
        </button>
        <button onClick={() => setCamera((c) => ({ ...c, yaw: 0, pitch: Math.PI / 2 - 0.001 }))}>
          Top
        </button>
        <button onClick={() => setCamera((c) => ({ ...c, yaw: 0.55, pitch: 0.3 }))}>
          Orbit view
        </button>
      </div>
      <div className="rig-view-hint">
        Drag a joint to {tool === 'add' ? 'place' : tool} · Alt/right drag orbit · Middle drag pan ·
        Scroll zoom{mode === 'skin' ? ' · Paint the selected mesh' : ''}
      </div>
      {renderError && (
        <div className="rig-preview-error" role="alert">
          {renderError}
        </div>
      )}
    </div>
  );
}
